import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Monitor,
  Smartphone,
  Users,
  Palette,
  Share2,
  CheckCircle2,
  TrendingDown,
  TrendingUp,
  Target,
  Zap,
  RefreshCcw
} from 'lucide-react';

const ServiceHero = () => {
  return (
    <section className="py-20 md:py-32 grid-bg relative" data-testid="services-hero">
      <div className="hero-glow -top-40 left-1/2 -translate-x-1/2 opacity-30" />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
          Our Services
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Comprehensive performance marketing solutions designed to drive measurable growth and sustainable ROAS.
        </p>
      </div>
    </section>
  );
};

const ServiceSection = ({ 
  id, 
  icon: Icon, 
  title, 
  description, 
  features, 
  focusTitle, 
  focusItems,
  reverse = false 
}) => {
  return (
    <section 
      id={id}
      className={`py-20 md:py-24 ${reverse ? 'bg-surface' : 'grid-bg'}`}
      data-testid={`service-${id}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid lg:grid-cols-2 gap-16 items-start ${reverse ? 'lg:flex-row-reverse' : ''}`}>
          <div className={reverse ? 'lg:order-2' : ''}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-lg bg-neon-green/10 neon-border flex items-center justify-center">
                <Icon className="w-7 h-7 text-neon-green" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white">
                {title}
              </h2>
            </div>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {description}
            </p>
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-4">What We Deliver:</h3>
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neon-green flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className={`glass rounded-xl p-8 ${reverse ? 'lg:order-1' : ''}`}>
            <h3 className="font-heading text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-neon-cyan" />
              {focusTitle}
            </h3>
            <div className="space-y-4">
              {focusItems.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/5"
                >
                  {item.icon && <item.icon className="w-5 h-5 text-neon-green" />}
                  <span className="text-gray-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      id: 'display-advertising',
      icon: Monitor,
      title: 'Display Advertising',
      description: 'Precision-targeted display campaigns across premium networks and programmatic ecosystems.',
      features: [
        'Audience segmentation',
        'Behavioral targeting',
        'Retargeting funnels',
        'Contextual placements',
        'Dynamic creative optimization',
        'Programmatic buying'
      ],
      focusTitle: 'Performance Focus',
      focusItems: [
        { icon: TrendingDown, text: 'Lower CPM' },
        { icon: TrendingUp, text: 'Higher CTR' },
        { icon: Target, text: 'Improved conversion rates' },
        { icon: RefreshCcw, text: 'Scalable remarketing ROAS' }
      ],
      reverse: false
    },
    {
      id: 'mobile-marketing',
      icon: Smartphone,
      title: 'Mobile Marketing',
      description: 'Built for apps and gaming studios seeking scalable user acquisition.',
      features: [
        'CPI & CPA optimization',
        'Android & iOS campaigns',
        'In-app event optimization',
        'Retention-focused targeting',
        'LTV-based bidding models',
        'Ad monetization strategy'
      ],
      focusTitle: 'We Optimize For',
      focusItems: [
        { text: 'Cost Per Install' },
        { text: 'Day 1/7/30 Retention' },
        { text: 'ARPU & ARPPU' },
        { text: 'In-app purchase value' },
        { text: 'Long-term ROAS' }
      ],
      reverse: true
    },
    {
      id: 'lead-generation',
      icon: Users,
      title: 'Lead Generation',
      description: 'High-intent lead acquisition for fintech, education, real estate, and B2B brands.',
      features: [
        'Funnel mapping',
        'Landing page optimization',
        'Conversion rate optimization',
        'Lead scoring integration',
        'CRM sync tracking',
        'Multi-touch attribution'
      ],
      focusTitle: 'Focus',
      focusItems: [
        { text: 'Cost per Qualified Lead' },
        { text: 'Sales conversion ratio' },
        { text: 'Revenue attribution' },
        { text: 'Lead quality scoring' }
      ],
      reverse: false
    },
    {
      id: 'creative-solutions',
      icon: Palette,
      title: 'Creative Solutions',
      description: 'Creative is the biggest performance lever. We design, test, and iterate high-converting ad creatives using data-backed frameworks.',
      features: [
        'Static & motion ads',
        'Video performance creatives',
        'UGC-style creatives',
        'Gaming ad concepts',
        'A/B testing frameworks',
        'Creative fatigue analysis'
      ],
      focusTitle: 'Method',
      focusItems: [
        { icon: Zap, text: 'Test → Learn → Scale → Refresh' },
        { text: 'Data-driven creative iteration' },
        { text: 'Performance-first design' },
        { text: 'Rapid concept development' }
      ],
      reverse: true
    },
    {
      id: 'social-media-marketing',
      icon: Share2,
      title: 'Social Media Marketing',
      description: 'Performance-driven social campaigns across Meta, Google Ads, X, Snapchat, and other leading Networks/DSPs.',
      features: [
        'Paid social scaling',
        'App install campaigns',
        'Conversion campaigns',
        'Retargeting loops',
        'Influencer amplification',
        'Cross-platform optimization'
      ],
      focusTitle: 'We Specialize In',
      focusItems: [
        { text: 'Meta (Facebook & Instagram)' },
        { text: 'Google Ads & YouTube' },
        { text: 'X (Twitter)' },
        { text: 'Snapchat' },
        { text: 'TikTok & Emerging Platforms' }
      ],
      reverse: false
    }
  ];

  return (
    <div data-testid="services-page">
      <ServiceHero />
      
      {services.map((service, index) => (
        <ServiceSection key={service.id} {...service} />
      ))}
      
      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-surface relative overflow-hidden" data-testid="services-cta">
        <div className="hero-glow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
            Ready to <span className="text-neon-green">Supercharge</span> Your Growth?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Let's discuss how our services can be tailored to your specific goals and KPIs.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="services-cta-btn">
            Book a Strategy Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
